import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ChoiceRevealOnScroll from "../shared/ChoiceRevealOnScroll.jsx";
import { fadeUpVariants, staggerContainer } from "../shared/animations.js";

const ParadigmHumanIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ParadigmMachineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M9 9h.01M15 9h.01" />
    <path d="M9 15h6" />
  </svg>
);

const ParadigmLeverageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const ParadigmShiftSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    {
      tag: "HUMAN",
      title: "The Human Premium",
      description:
        "Your team stays on what humans do best—empathy, trust, and the final close. No role changes, no layoffs: the digital employee handles the front of the funnel so every conversation your people have is worth their time.",
      icon: <ParadigmHumanIcon />,
      highlight: false,
    },
    {
      tag: "MACHINE",
      title: "Machine Determinism",
      description:
        "Every inbound lead gets a reply in 9 seconds—including nights and weekends. The digital employee runs your qualification protocol, logs every detail, and books qualified meetings to the calendar. No fatigue, no missed threads, no CRM gaps.",
      icon: <ParadigmMachineIcon />,
      highlight: false,
    },
    {
      tag: "2x APPOINTMENTS",
      title: "Qualified Pipeline, Not Headcount",
      description:
        "Same ad spend, zero new hires—up to 2x more qualified appointments on the calendar. Speed wins the lead; protocol filters the noise; your team only talks to prospects who are already vetted and booked.",
      icon: <ParadigmLeverageIcon />,
      highlight: true,
    },
  ];

  const EquationConnector = ({ symbol, testId }) => (
    <motion.div
      className="flex items-center justify-center lg:px-4 py-2 lg:py-0 flex-shrink-0"
      variants={fadeUpVariants}
      data-testid={testId}
    >
      <span className="text-2xl font-mono text-purple-400/60">{symbol}</span>
    </motion.div>
  );

  const EquationZone = ({ pillar, index }) => (
    <motion.div
      className={`relative flex-1 min-w-0 rounded-xl p-5 sm:p-6 ${
        pillar.highlight
          ? "border border-purple-500/30 bg-purple-500/[0.08] shadow-[0_0_30px_rgba(168,85,247,0.08)]"
          : ""
      }`}
      variants={fadeUpVariants}
      data-testid={`paradigm-pillar-${index}`}
    >
      {pillar.highlight && (
        <span className="absolute top-3 right-3 badge-mono text-[10px] text-purple-300 bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 rounded-full">
          2x
        </span>
      )}
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/25 flex items-center justify-center text-purple-400">
          {pillar.icon}
        </div>
        <span className="badge-mono text-[10px] text-purple-400/80 tracking-widest pt-3">{pillar.tag}</span>
      </div>
      <h3 className="text-lg sm:text-xl font-bold mb-2 text-white">{pillar.title}</h3>
      <p className="text-sm sm:text-base text-gray-400 font-body leading-relaxed">{pillar.description}</p>
    </motion.div>
  );

  return (
    <section className="section-padding bg-[#050810] relative overflow-hidden" data-testid="paradigm-shift-section" ref={ref}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(168,85,247,0.06),transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <ChoiceRevealOnScroll className="text-center mb-10">
          <span className="badge-mono text-purple-400 bg-purple-500/10 border border-purple-500/20 px-3 py-1.5 rounded-full inline-block mb-6">
            THE PARADIGM SHIFT
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight" data-testid="paradigm-shift-headline">
            The Architecture of <span className="gradient-text">Amplification.</span>
          </h2>
          <p className="text-lg text-gray-400 font-body leading-relaxed max-w-3xl mx-auto mb-6" data-testid="paradigm-shift-intro">
            <span className="text-white font-semibold">A digital employee for your team—not a replacement.</span>{" "}
            Live in <span className="text-purple-400 font-semibold">72 hours</span>, same ad spend,{" "}
            <span className="text-purple-400 font-semibold">zero new hires</span>: it qualifies, logs, and books; your people close.
          </p>
          <div
            className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-2 rounded-full border border-purple-500/20 bg-purple-500/[0.06] mb-10"
            data-testid="paradigm-shift-thesis"
          >
            <span className="text-gray-500 line-through text-sm font-mono">Hire for lead volume</span>
            <span className="text-purple-400">→</span>
            <span className="text-white font-semibold text-sm">9-second reply · 2x qualified appointments</span>
          </div>
        </ChoiceRevealOnScroll>

        <motion.div
          className="bento-card border border-purple-500/20 bg-gradient-to-r from-purple-500/[0.05] via-white/[0.02] to-purple-500/[0.05] p-6 sm:p-8 lg:p-10"
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          data-testid="paradigm-equation-panel"
        >
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-2 lg:gap-0">
            <EquationZone pillar={pillars[0]} index={0} />
            <EquationConnector symbol="+" testId="paradigm-connector-plus" />
            <EquationZone pillar={pillars[1]} index={1} />
            <EquationConnector symbol="=" testId="paradigm-connector-equals" />
            <EquationZone pillar={pillars[2]} index={2} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ParadigmShiftSection;
